
import React from 'react';
import { Link } from 'react-router-dom';
import PageWrapper from '@/components/utils/PageWrapper';
import AnimatedElement from '@/components/utils/AnimatedElement';
import { Bot, ArrowLeft, ChevronRight } from 'lucide-react';
import AnimatedLink from '@/components/ui/animated-link';
import { motion } from 'framer-motion';

const ArtificialIntelligencePage = () => {
  return (
    <PageWrapper>
      <main className="flex-grow pt-32 pb-20">
        <div className="container mx-auto px-6">
          <AnimatedElement animation="fadeIn">
            <div className="mb-6">
              <Link to="/services" className="text-muted-foreground hover:text-primary flex items-center transition-colors">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to all services
              </Link>
            </div>
          </AnimatedElement>

          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="md:col-span-2">
              <AnimatedElement animation="fadeIn" delay={0.1}>
                <div className="bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400 p-3 rounded-xl inline-block mb-4">
                  <Bot className="h-10 w-10" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
                  Artificial <span className="text-primary">Intelligence</span>
                </h1>
                <p className="text-xl text-muted-foreground mb-6">
                  Advanced AI systems that solve complex problems and enhance decision-making processes.
                </p>
              </AnimatedElement>

              <AnimatedElement animation="slideUp" delay={0.2} className="prose prose-lg dark:prose-invert max-w-none">
                <p>
                  Artificial Intelligence represents the cutting edge of technology, enabling systems to perform tasks that typically require human intelligence. Our AI solutions help businesses automate complex processes, gain deeper insights, and make better decisions faster.
                </p>
                
                <h2>Our Approach</h2>
                <p>
                  We develop custom AI solutions that address your specific business challenges. Our approach combines state-of-the-art algorithms with domain expertise to create systems that deliver tangible business value and competitive advantage.
                </p>
                
                <h2>Key Benefits</h2>
                <ul>
                  <li>Automate complex cognitive tasks</li>
                  <li>Process and analyze vast amounts of structured and unstructured data</li>
                  <li>Enhance decision-making with intelligent recommendations</li>
                  <li>Improve customer experiences through personalization</li>
                  <li>Identify new opportunities and mitigate risks</li>
                  <li>Gain sustainable competitive advantage</li>
                </ul>
              </AnimatedElement>
            </div>

            <div className="md:col-span-1">
              <AnimatedElement animation="scale" delay={0.3}>
                <motion.div 
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 shadow-subtle border border-gray-100 dark:border-gray-700 sticky top-32"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300, damping: 15 }}
                >
                  <h3 className="text-xl font-semibold mb-4">Our AI Services</h3>
                  <ul className="space-y-3">
                    {[
                      "Cognitive Computing",
                      "Expert Systems",
                      "Autonomous Agents",
                      "Knowledge Representation",
                      "AI Strategy Consulting",
                      "Process Automation"
                    ].map((service, index) => (
                      <motion.li 
                        key={index}
                        className="flex items-start"
                        whileHover={{ x: 5 }}
                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                      >
                        <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                        <span>{service}</span>
                      </motion.li>
                    ))}
                  </ul>

                  <div className="mt-8">
                    <AnimatedLink to="/contact" fullWidth>
                      Contact Us
                    </AnimatedLink>
                  </div>
                </motion.div>
              </AnimatedElement>
            </div>
          </div>

          <AnimatedElement animation="fadeIn" delay={0.4}>
            <motion.div 
              className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 p-8 rounded-2xl mb-16"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
            >
              <h2 className="text-2xl font-semibold mb-4">Ready to leverage AI for your business?</h2>
              <p className="text-muted-foreground mb-6">
                Let's discuss how our AI solutions can transform your operations and drive innovation.
              </p>
              <AnimatedLink to="/contact">
                Get Started
              </AnimatedLink>
            </motion.div>
          </AnimatedElement>
        </div>
      </main>
    </PageWrapper>
  );
};

export default ArtificialIntelligencePage;
